import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { ArrowRight, CircleNotch } from "@phosphor-icons/react";

/**
 * Visual storyboard for the public landing page.
 *
 * Pulls real example outputs from the admin account (selfie → AI Twin →
 * cinematic motion → finished reel) via GET /api/admin/showcase. Renders as
 * a 4-step horizontal flow on desktop, vertical card stack on mobile.
 *
 * Falls back silently (returns null) if the API doesn't return any steps —
 * so we never show a broken empty section.
 */
export default function VisualShowcase() {
  const [steps, setSteps] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/showcase`);
        if (!alive) return;
        setSteps(res.data?.steps || []);
        setMeta({
          headline: res.data?.headline || "Watch it work, end to end",
          subhead: res.data?.subhead || "",
        });
      } catch (_) {
        // silent — landing page must not error out
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <section className="max-w-6xl mx-auto px-4 py-16 text-center">
        <CircleNotch size={28} className="animate-spin text-zinc-300 mx-auto" />
      </section>
    );
  }
  if (!steps.length) return null;

  return (
    <section
      className="relative bg-gradient-to-b from-white via-pink-50/30 to-white border-y border-pink-100/60"
      data-testid="landing-visual-showcase"
    >
      <div className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
        <p className="text-center text-xs font-bold uppercase tracking-widest text-[#E91E8C] mb-3">See it in action</p>
        <h2 className="text-center text-3xl sm:text-4xl font-extrabold text-zinc-900 mb-2 tracking-tight">
          {meta.headline}
        </h2>
        {meta.subhead && (
          <p className="text-center text-zinc-500 text-base sm:text-lg mb-12 max-w-2xl mx-auto">{meta.subhead}</p>
        )}

        <div className="relative">
          {/* connector dots between cards on desktop */}
          <div className="hidden lg:block absolute top-[155px] left-0 right-0 h-px pointer-events-none">
            <div className="max-w-5xl mx-auto h-px bg-gradient-to-r from-transparent via-[#E91E8C]/30 to-transparent" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {steps.map((s, idx) => (
              <ShowcaseCard key={s.n} step={s} isLast={idx === steps.length - 1} />
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-zinc-400 mt-10">
          Every example above was created in this exact app — by a real creator account, in under 5 minutes.
        </p>
      </div>
    </section>
  );
}

function ShowcaseCard({ step, isLast }) {
  const videoRef = useRef(null);
  // If the cdn-hosted mp4 fails to play (codec / network / autoplay block),
  // fall back to rendering the poster image at full opacity instead of a
  // ghosted, broken-looking video tag.
  const [videoFailed, setVideoFailed] = useState(false);

  // Always preload metadata so the first frame paints as a poster, even
  // before the user scrolls down. Once the card scrolls into view we kick
  // off autoplay; out of view we pause to keep CPU/bandwidth modest.
  useEffect(() => {
    if (step.type !== "video" || videoFailed) return;
    const el = videoRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            try { el.play().catch(() => {}); } catch (_) {}
          } else {
            try { el.pause(); } catch (_) {}
          }
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [step.type, videoFailed]);

  return (
    <div
      className="relative bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
      data-testid={`showcase-step-${step.n}`}
    >
      {/* number badge */}
      <div className="absolute top-3 left-3 z-10 w-9 h-9 rounded-full bg-[#E91E8C] text-white flex items-center justify-center font-extrabold text-sm shadow-lg ring-2 ring-white">
        {step.n}
      </div>

      {/* desktop chevron between cards */}
      {!isLast && (
        <div className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-white border border-pink-200 items-center justify-center shadow-sm">
          <ArrowRight size={12} weight="bold" className="text-[#E91E8C]" />
        </div>
      )}

      {/* media — fixed 9:14 ratio so cards line up across the row */}
      <div className="relative aspect-[9/14] bg-gradient-to-br from-zinc-100 to-zinc-50 overflow-hidden">
        {step.type === "image" ? (
          <img
            src={step.url}
            alt={step.label}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
          />
        ) : videoFailed && step.poster ? (
          // Video src unsupported in this browser — keep the poster visible
          // at full opacity so the card never looks broken.
          <img
            src={step.poster}
            alt={step.label}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          />
        ) : (
          <video
            ref={videoRef}
            src={step.url}
            poster={step.poster || undefined}
            muted
            loop
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
            onError={() => setVideoFailed(true)}
          />
        )}
        {/* gradient overlay for label legibility */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/55 via-black/15 to-transparent pointer-events-none" />
        <p className="absolute bottom-2.5 left-3 right-3 text-white text-[11px] font-semibold drop-shadow-lg leading-tight line-clamp-2">
          {step.label}
        </p>
      </div>

      {/* caption */}
      <div className="p-4 sm:p-5">
        <h3 className="text-sm sm:text-base font-bold text-zinc-900 mb-1.5">{step.title}</h3>
        <p className="text-xs sm:text-[13px] text-zinc-500 leading-relaxed">{step.caption}</p>
      </div>
    </div>
  );
}
